import { useCallback, useEffect, useRef, useState } from "react";

export const BROWSER_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
export const BROWSER_REQUEST_TIMEOUT_MS = 20 * 1000;

export interface ApiResource<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  isRefreshing: boolean;
  refreshedAt: Date | null;
  refresh: () => void;
}

export function useApiResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  refreshIntervalMs = BROWSER_REFRESH_INTERVAL_MS,
): ApiResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const hasDataRef = useRef(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(() => {
    requestRef.current?.abort();
    const controller = new AbortController();
    let timedOut = false;
    requestRef.current = controller;
    if (hasDataRef.current) setIsRefreshing(true);
    else setIsLoading(true);
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, BROWSER_REQUEST_TIMEOUT_MS);

    void loader(controller.signal)
      .then((nextData) => {
        if (controller.signal.aborted || requestRef.current !== controller) return;
        hasDataRef.current = true;
        setData(nextData);
        setError(null);
        setRefreshedAt(new Date());
      })
      .catch((reason: unknown) => {
        if (requestRef.current !== controller) return;
        if (timedOut) {
          setError(new Error("The request timed out. Try refreshing the page."));
          return;
        }
        if (controller.signal.aborted) return;
        setError(
          reason instanceof Error ? reason : new Error("The request could not be completed"),
        );
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (requestRef.current !== controller) return;
        requestRef.current = null;
        if (!mountedRef.current) return;
        setIsLoading(false);
        setIsRefreshing(false);
      });
  }, [loader]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
      requestRef.current?.abort();
    };
  }, [refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, refreshIntervalMs);
    return () => window.clearInterval(timer);
  }, [refresh, refreshIntervalMs]);

  return { data, error, isLoading, isRefreshing, refreshedAt, refresh };
}
